import * as THREE from 'three';
import { clamp, damp } from '@/math/angles';
import { CHASE_ELEVATION, capeShape } from './capeShape';
import { PartBuilder, clothMesh, cornerFlagTexture, type ClothHandle } from './modKit';
import type { ModAnchors } from './modAnchors';
import { fanLayout, fanVolume, restTopAt } from './modProfile';
import type { SurfaceField } from './modSurface';
import type { ModCtx, PartFn } from './modTypes';
import { GLOVE_REACH, clearOfWing, floodSpots, gloveSpots, trophyScale, trophySpot } from './partsHard';
import { goalFrameFor, tifoSpec } from './partsSoft';

/**
 * partsFlags.ts (M154 r2) — the Corner Flags, and the room every OTHER
 * standing mod takes on a ride, which the flags keep clear of.
 *
 * Round 1 kept the poles and cloths clear of the floodlight masts only; the
 * in-game review then found both flags hidden behind the monster truck's
 * tailgate crew, the Formula's near cloth hanging across a lamp head, the
 * bike's flag still between its two towers, and cloths seen edge-on behind a
 * tower from the three-quarter view. Now the flags stand where every worn
 * standing mod (the masts and their lamp heads, the cheering fans with their
 * arms and scarves up, the trophy, the gloves, the Goal Wing) leaves room for
 * pole AND cloth, off the cape's cloth, and where the race camera and the
 * three-quarter view both see most of each cloth; failing the rear quarters,
 * the front quarters (the bike: anywhere along its top).
 */

const R = (deg: number): number => (deg * Math.PI) / 180;
const V = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);
const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));
const windOf = (ctx: ModCtx): number => clamp01(ctx.speed / 35);
const WHITE = 0xf1f1ee;
const GOLD = 0xe8b23a;
const RUBBER = 0x24252a;

/** The flag's pole height, cloth length and depth, and its droop at rest
 *  (units of the ride's part scale s). */
const POLE_H = 1.15;
const CLOTH_L = 0.52;
const CLOTH_W = 0.34;
const DROOP = 0.5;

/** How far the cloth splays out from straight astern (wind 0..1): straight
 *  back it was edge-on to the chase camera (M153). */
export function flagSplay(wind: number): number {
  return R(38) - R(15) * wind;
}

/** A volume another standing mod takes: an upright cylinder (`mast` = a
 *  floodlight tower's lattice or lamp head). */
export interface ModVolume {
  x: number;
  z: number;
  r: number;
  y0: number;
  y1: number;
  mast?: boolean;
  /** (polish r5) the Trophy Topper's cup */
  trophy?: boolean;
}

/** M154 r2: the room every worn standing mod takes (pure: the flags and the
 *  tests share it). The flags themselves are not in it. */
export function standingVolumes(a: ModAnchors, surf: SurfaceField | null, mods: readonly string[]): ModVolume[] {
  const s = a.s;
  const out: ModVolume[] = [];
  const top = (x: number, z: number, fallback: number): number => surf?.topAt(x, z) ?? fallback;
  if (mods.includes('mod.floods')) {
    const { spots, H } = floodSpots(a, surf, mods);
    for (const m of spots) {
      // the lattice, then the lamp head over it (a box half a part wide,
      // yawed and tipped forward)
      out.push({ x: m.x, z: m.z, r: 0.17 * s, y0: m.y, y1: m.y + H, mast: true });
      out.push({ x: m.x, z: m.z, r: 0.29 * s, y0: m.y + H - 0.04 * s, y1: m.y + H + 0.32 * s, mast: true });
    }
  }
  if (mods.includes('mod.fans')) for (const f of fanLayout(a, surf, mods)) out.push(fanVolume(f));
  if (mods.includes('mod.trophy')) {
    const t = trophySpot(a, surf);
    const T = trophyScale(a) * t.k;
    const y = a.kind === 'bike' ? a.hood.y : top(t.x, t.z, a.hood.y);
    out.push({ x: t.x, z: t.z, r: 0.16 * T, y0: y, y1: y + 0.69 * T, trophy: true });
  }
  if (mods.includes('mod.gloves')) {
    const g = (a.kind === 'bike' ? 0.8 : 1) * s;
    for (const p of gloveSpots(a, surf, mods)) {
      const y = a.kind === 'bike' && p.z === a.fender.z ? a.fender.y : top(p.x, p.z, a.fender.y);
      out.push({ x: p.x, z: p.z, r: GLOVE_REACH * g * p.k, y0: y, y1: y + 0.8 * g * p.k });
    }
  }
  if (mods.includes('mod.goalwing') && a.kind === 'car') {
    const { span, h, y, z } = goalFrameFor(a, surf, mods);
    // the posts, and the crossbar with its wing plane as a row of drums
    for (const sx of [-1, 1]) out.push({ x: (sx * span) / 2, z, r: 0.08 * s, y0: y, y1: y + h + 0.3 * s });
    const n = Math.max(2, Math.ceil((span + 0.3 * s) / (0.3 * s)));
    for (let i = 0; i <= n; i++) {
      const x = -span / 2 - 0.15 * s + ((span + 0.3 * s) * i) / n;
      out.push({ x, z, r: 0.2 * s, y0: y + h - 0.06 * s, y1: y + h + 0.3 * s });
    }
  }
  return out;
}

/** M154 r2: the rider of a bike (an upright drum over the seat, the tucked
 *  rider and his helmet): painted like the bike, so the measured surface
 *  cannot tell him from bodywork (a flag stood on the helmet). Null on rides
 *  whose occupant sits inside the body. */
export function riderVolumes(a: ModAnchors): ModVolume[] {
  if (a.kind !== 'bike' || !a.helmetBack) return [];
  const h = a.helmetBack;
  // the tucked torso and helmet, and the arms reaching forward to the bars
  return [
    { x: 0, z: h.z - 0.2 * a.k, r: 0.34 * a.k, y0: 0, y1: h.y + 0.3 * a.k },
    { x: 0, z: h.z + 0.32 * a.k, r: 0.3 * a.k, y0: 0, y1: h.y + 0.1 * a.k },
  ];
}

/** A flag pole's foot: where it stands and which way (+1 = +X) its cloth
 *  flies. */
export interface FlagBase {
  sx: number;
  x: number;
  z: number;
}

/** Points over a flag's cloth (car-local) on a pole whose foot is at
 *  (x, y, z), flying toward `dir`, at rest (wind 0: splayed out, the free end
 *  drooping) or at speed (wind 1: streaming nearly straight back, level). */
export function flagClothPoints(x: number, y: number, z: number, dir: number, s: number, wind: number): THREE.Vector3[] {
  const sp = flagSplay(wind);
  const cx = dir * Math.sin(sp);
  const cz = -Math.cos(sp);
  const top = y + (POLE_H - 0.02) * s;
  const out: THREE.Vector3[] = [];
  for (const u of [0.15, 0.4, 0.65, 0.9]) {
    for (const v of [0.15, 0.5, 0.85]) {
      const d = u * CLOTH_L * s;
      out.push(V(x + cx * d, top - v * CLOTH_W * s - DROOP * s * (1 - wind) * Math.pow(u, 1.6), z + cz * d));
    }
  }
  return out;
}

/** The directions toward the cameras the flags are judged from (car-local):
 *  the race camera (behind, CHASE_ELEVATION up), and the livery lab's and
 *  garage's three-quarter view from the rear right (the shop sheets). */
export const FLAG_VIEWS: readonly THREE.Vector3[] = [
  V(0, Math.sin(CHASE_ELEVATION), -Math.cos(CHASE_ELEVATION)),
  V(Math.SQRT1_2 * Math.cos(0.55), Math.sin(0.55), -Math.SQRT1_2 * Math.cos(0.55)),
  // M154 polish r2: and the front three-quarter view (the shop's and the
  // garage's hero angle): from there the cloths crossed the floodlight
  // towers' lattice on the coupe, the sports car and the Formula.
  V(Math.SQRT1_2 * Math.cos(0.36), Math.sin(0.36), Math.SQRT1_2 * Math.cos(0.36)),
];

/** How face on a camera along `v` sees a cloth flying toward `dir` at rest
 *  (1 = square on, 0 = edge on), in plan. */
export function flagFaceOn(dir: number, v: THREE.Vector3): number {
  const sp = flagSplay(0);
  const cx = dir * Math.sin(sp);
  const cz = -Math.cos(sp);
  const h = Math.hypot(v.x, v.z) || 1;
  return Math.abs(cx * v.z - cz * v.x) / h;
}

/** True when the ray from `p` toward the camera (`d`, unit) passes through
 *  the volume. */
export function rayHitsVolume(p: THREE.Vector3, d: THREE.Vector3, v: ModVolume): boolean {
  const px = p.x - v.x;
  const pz = p.z - v.z;
  const a = d.x * d.x + d.z * d.z;
  const b = 2 * (px * d.x + pz * d.z);
  const c = px * px + pz * pz - v.r * v.r;
  if (c <= 0) {
    // already inside the drum in plan: hidden if it is at the drum's height,
    // or the ray climbs into it
    if (p.y <= v.y1 && p.y >= v.y0) return true;
    if (p.y < v.y0) {
      if (a < 1e-12) return true;
      const disc = b * b - 4 * a * c;
      const t2 = (-b + Math.sqrt(Math.max(0, disc))) / (2 * a);
      return p.y + t2 * d.y >= v.y0;
    }
    return false;
  }
  if (a < 1e-12) return false;
  const disc = b * b - 4 * a * c;
  if (disc <= 0) return false;
  const sq = Math.sqrt(disc);
  const t1 = (-b - sq) / (2 * a);
  const t2 = (-b + sq) / (2 * a);
  if (t2 <= 0) return false;
  const ta = Math.max(0, t1);
  const ya = p.y + ta * d.y;
  const yb = p.y + t2 * d.y;
  return Math.max(ya, yb) >= v.y0 && Math.min(ya, yb) <= v.y1;
}

/** The share of `pts` the camera along `d` sees past the volumes (and the
 *  measured body), not counting the points with one of the `behind` volumes
 *  BEHIND them either (a cloth drawn over a floodlight tower's lattice reads
 *  as part of the tower). */
export function visibleShare(
  pts: readonly THREE.Vector3[],
  d: THREE.Vector3,
  vols: readonly ModVolume[],
  surf: SurfaceField | null,
  behind: readonly ModVolume[] = [],
): number {
  let seen = 0;
  const q = new THREE.Vector3();
  const back = d.clone().negate();
  for (const p of pts) {
    let hidden = vols.some((v) => rayHitsVolume(p, d, v)) || behind.some((v) => rayHitsVolume(p, back, v));
    if (!hidden && surf) {
      for (let t = 0.06; t < 3; t += 0.06) {
        q.copy(p).addScaledVector(d, t);
        const top = surf.topAt(q.x, q.z);
        if (top !== null && q.y < top - 0.02) {
          hidden = true;
          break;
        }
      }
    }
    if (!hidden) seen++;
  }
  return pts.length ? seen / pts.length : 1;
}

/** The distance from (px, pz) to the segment a-b (in plan). */
export function segmentDistance(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const dx = bx - ax;
  const dz = bz - az;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / (dx * dx + dz * dz || 1)));
  return Math.hypot(px - (ax + dx * t), pz - (az + dz * t));
}

/** How much of each cloth of a candidate the cameras must see. */
const SEEN_CHASE = 0.6;
const SEEN_ISO = 0.5;
const SEEN_FRONT = 0.9;
/** M154 polish r5: and how much of each cloth (and its pole's top) the race
 *  camera and the three-quarter view see clear of a floodlight tower or the
 *  trophy, in front of it or behind it: at the old shares half a cloth could
 *  lie across a tower's lattice (the coupe, the sports car) or behind the
 *  trophy (the monster truck), where live kept them apart. */
export const SEEN_CLEAR = 0.95;
/** ...judged against each tower and the trophy grown by this much. */
export const CLEAR_PAD = 1.3;
/** ...on the rides where they crossed: the monster truck (the trophy hid a
 *  pole and half a cloth). The coupe and the sports car were on this list
 *  too, but clearing their towers turned both cloths inward until they
 *  crossed each other over the rear glass from the race camera (the round-7
 *  fleet review), worse than a cloth touching a lattice from the side, so
 *  they keep their round-6 places, as does everything else (the Formula's
 *  would leave its rear wing for the sidepods, the pickup's the trophy for
 *  the fans in its bed). */
export const FLAG_CLEAR_RIDES: ReadonlySet<string> = new Set(['van']);

/**
 * Where the corner flags' poles stand (pure: the part and the tests share
 * it). The rear quarters (forward of a stock wing) when nothing else is worn
 * there; otherwise the nearest spot, rear quarters first, then the front
 * quarters (the bike: along its top), where:
 *  - pole and cloth (at rest and at speed) keep clear of every other worn
 *    standing mod (standingVolumes) and of the body;
 *  - the pole stands on paint, on the body (not past an edge), off the
 *    cape's cloth;
 *  - the race camera and the three-quarter view each see most of each cloth
 *    past those mods.
 * The cloth flies outward, or inward when that is what clears; the pair is
 * mirrored side to side (the bike flies one).
 */
export function flagBases(a: ModAnchors, surf: SurfaceField | null, mods: readonly string[]): FlagBase[] {
  const s = a.s;
  const rear: FlagBase[] =
    a.kind === 'bike'
      ? [{ sx: 1, x: 0, z: a.tailZ + 0.05 * a.k }]
      : a.kind === 'f1'
        ? [-1, 1].map((sx) => ({ sx, x: sx * 0.56 * a.k, z: a.deckZ }))
        : [-1, 1].map((sx) => ({ sx, x: sx * a.halfW * 0.86, z: clearOfWing(a, a.deckZ, 0.12 * s) }));
  const vols = standingVolumes(a, surf, mods);
  // (seen from the race camera, a cloth must not be drawn over a tower; from
  // the three-quarter view, over none of the other mods)
  const masts = vols.filter((v) => v.mast);
  // (polish r5: the towers and the trophy, which no cloth may cross)
  // (with a hand of air round each: a cloth's tip touching the cup's globe or
  // a tower's lattice still reads as crossing it)
  const keep = FLAG_CLEAR_RIDES.has(a.id) ? vols.filter((v) => v.mast || v.trophy).map((v) => ({ ...v, r: v.r * CLEAR_PAD })) : [];
  // (the rider is painted like the bike, helmet and all: never a flag's foot)
  const rider = riderVolumes(a);
  const cape = mods.includes('mod.tifo') ? capeShape(surf, tifoSpec(a, surf, mods), (x, z) => restTopAt(a, null, x, z, true)) : null;
  if (!vols.length && !cape) return rear;
  const seat = (x: number, z: number): number => surf?.topIn(x - 0.06 * s, x + 0.06 * s, z - 0.06 * s, z + 0.06 * s) ?? a.deckY;
  const pair = rear.length === 2;
  /** Hard rules for one flag (and its mirror). */
  const clearAt = (x: number, z: number, dir: number, y0: number | null, paint: boolean): boolean => {
    const y = seat(x, z);
    if (surf) {
      if (y0 !== null && Math.abs(y - y0) > 0.3 * s) return false;
      // the foot stands ON the body, with body ahead of, behind and beside
      // it at about its height (not on an edge, nor in the air behind a
      // mirror)
      // (the bike's narrow tail and nose: the foot's own width)
      const e = (a.kind === 'bike' ? 0.06 : 0.1) * s;
      for (const [ox, oz] of [[0, -e], [0, e], [-e, 0], [e, 0]] as const) {
        const t = surf.topIn(x + ox - 0.01, x + ox + 0.01, z + oz - 0.01, z + oz + 0.01);
        if (t === null || Math.abs(t - y) > 0.12 * s) return false;
      }
      // a pole whose home is on paint stays on paint (never onto glass, a
      // vent or the rider; the Formula's home is its rear wing's top)
      if (paint && surf.openDistance(x, z, 0.2) <= 0.06 * s) return false;
    }
    if (cape) {
      for (const [ox, oz] of [[0, 0], [0.06, 0], [-0.06, 0], [0, 0.06], [0, -0.06]] as const) {
        if (cape.restAt(x + ox * s, z + oz * s) !== null) return false;
      }
    }
    for (const sx of pair ? [1, -1] : [1]) {
      const px = sx * x;
      const d = sx * dir;
      for (const v of [...vols, ...rider]) {
        if (Math.hypot(v.x - px, v.z - z) < v.r + 0.05 * s && v.y1 > y && v.y0 < y + POLE_H * s) return false;
      }
      for (const wind of [0, 1]) {
        for (const p of flagClothPoints(px, y, z, d, s, wind)) {
          for (const v of vols) if (Math.hypot(v.x - p.x, v.z - p.z) < v.r + 0.04 * s && p.y > v.y0 - 0.04 * s && p.y < v.y1 + 0.04 * s) return false;
          if (surf) {
            const t = surf.topAt(p.x, p.z);
            if (t !== null && p.y < t + 0.03) return false;
          }
          // two inward cloths never meet over the centreline
          if (pair && Math.sign(p.x) !== Math.sign(px) && Math.abs(p.x) > 0.02) return false;
        }
      }
    }
    return true;
  };
  /** How well the cameras see the cloths: 4 = each sees its share of
   *  each cloth and both see them clear of the towers and the trophy (a
   *  pass); less, how close it comes. */
  const seenAt = (x: number, z: number, dir: number): number => {
    const y = seat(x, z);
    let chase = 1;
    let iso = 1;
    let front = 1;
    let clear = 1;
    for (const sx of pair ? [1, -1] : [1]) {
      const px = sx * x;
      const d = sx * dir;
      const rest = flagClothPoints(px, y, z, d, s, 0);
      const fly = flagClothPoints(px, y, z, d, s, 1);
      chase = Math.min(
        chase,
        visibleShare(rest, FLAG_VIEWS[0]!, vols, surf, masts) / SEEN_CHASE,
        visibleShare(fly, FLAG_VIEWS[0]!, vols, surf, masts) / SEEN_CHASE,
      );
      // (a lone flag (the bike's) also flies the way the three-quarter view
      // sees it face on: flown toward that camera it read as a bare pole; a
      // mirrored pair always shows that view one cloth edge on)
      const face = pair ? 1 : flagFaceOn(d, FLAG_VIEWS[1]!);
      iso = Math.min(iso, (visibleShare(rest, FLAG_VIEWS[1]!, vols, surf, vols) * Math.min(1, face / 0.5)) / SEEN_ISO);
      // (polish r2: from the front three-quarter view, a cloth seen against
      // a tower, in front of it or behind it, does not count)
      if (masts.length) front = Math.min(front, visibleShare(rest, FLAG_VIEWS[2]!, masts, surf, masts) / SEEN_FRONT);
      if (keep.length) {
        // (the pole's top half too: the trophy stood in front of the monster
        // truck's far pole)
        const pole = [0.55, 0.75, 0.95].map((f) => V(px, y + f * POLE_H * s, z));
        for (const v of [FLAG_VIEWS[0]!, FLAG_VIEWS[1]!]) {
          for (const pts of [[...rest, ...pole], fly]) clear = Math.min(clear, visibleShare(pts, v, keep, null, keep) / SEEN_CLEAR);
        }
      }
    }
    return Math.min(1, chase) + Math.min(1, iso) + Math.min(1, front) + Math.min(1, clear);
  };
  const step = 0.03;
  type Cand = { x: number; z: number; d: number };
  const regions: { base: FlagBase; y0: number | null; paint: boolean; cands: Cand[] }[] = [];
  const onPaint = (b: FlagBase): boolean => !surf || surf.openDistance(b.x, b.z, 0.2) > 0.06 * s;
  const box = (base: FlagBase, inward: number, back: number, ahead: number, outward = 0): Cand[] => {
    const out: Cand[] = [];
    const sgn = Math.sign(base.x) || 1;
    for (let i = -Math.round(outward / step); i <= Math.round(inward / step); i++) {
      for (let j = -Math.round(back / step); j <= Math.round(ahead / step); j++) {
        const x = base.x - sgn * i * step;
        const z = base.z + j * step;
        if (z < base.z && a.wing && z < a.wing.z + a.wing.chord / 2 + 0.12 * s) continue;
        out.push({ x, z, d: Math.hypot(i * step, j * step) });
      }
    }
    return out.sort((p, q) => p.d - q.d);
  };
  if (a.kind === 'bike') {
    // the tail (its home), else the nose ahead of the screen (with the
    // floodlights on the tail's corners and the pillion on its seat, the tail
    // has no room left; M154 r2)
    const b = rear[0]!;
    const line = (base: FlagBase, back: number, ahead: number): Cand[] => {
      const cands: Cand[] = [];
      for (let j = -Math.round(back / step); j <= Math.round(ahead / step); j++) {
        for (const x of [0, 0.05, -0.05, 0.1, -0.1, 0.15, -0.15, 0.2, -0.2, 0.225, -0.225, 0.25, -0.25, 0.275, -0.275, 0.3, -0.3, 0.35, -0.35, 0.4, -0.4]) cands.push({ x: x * a.k, z: base.z + j * step, d: Math.abs(j * step) + Math.abs(x) });
      }
      return cands.sort((p, q) => p.d - q.d);
    };
    regions.push({ base: b, y0: seat(b.x, b.z), paint: true, cands: line(b, 0, 0.35 * a.k) });
    // (anywhere along the bike from the tail to the front mudguard, nearest
    // the tail first, never on the rider)
    regions.push({ base: b, y0: null, paint: true, cands: line(b, 0, a.noseZ + 0.1 * a.k - b.z) });
  } else {
    const r = rear.find((b) => b.sx > 0)!;
    regions.push({ base: r, y0: seat(r.x, r.z), paint: onPaint(r), cands: box(r, Math.max(0, Math.abs(r.x) - 0.12 * s), 0.4 * s, 1.0 * s) });
    const front: FlagBase =
      a.kind === 'f1' ? { sx: 1, x: Math.abs(a.door.x), z: a.door.z } : { sx: 1, x: Math.min(Math.abs(a.fender.x), a.halfW * 0.86), z: a.fender.z };
    regions.push({ base: front, y0: seat(front.x, front.z), paint: true, cands: box(front, Math.max(0, Math.abs(front.x) - 0.3 * s), 0.5 * s, 0.5 * s) });
  }
  const finish = (x: number, z: number, dir: number): FlagBase[] =>
    pair
      ? [
          { sx: -dir, x: -x, z },
          { sx: dir, x, z },
        ]
      : [{ sx: dir, x, z }];
  let best: { x: number; z: number; dir: number; seen: number } | null = null;
  // The cloths fly OUTWARD wherever that clears, rear or front; inward only
  // as a last resort (round 1's inward cloth hung across the coupe's rear
  // window).
  for (const inward of [false, true]) {
    for (const reg of regions) {
      for (const c of reg.cands) {
        // (out = away from the centreline: the bike's lone flag by its side)
        const out = a.kind === 'bike' ? Math.sign(c.x) || 1 : Math.sign(reg.base.sx) || 1;
        const dir = inward ? -out : out;
        if (!clearAt(c.x, c.z, dir, reg.y0, reg.paint)) continue;
        const seen = seenAt(c.x, c.z, dir);
        if (seen >= 4) return finish(c.x, c.z, dir);
        if (!best || seen > best.seen + 1e-9) best = { x: c.x, z: c.z, dir, seen };
      }
    }
  }
  return best ? finish(best.x, best.z, best.dir) : rear;
}

// ---------------------------------------------------------------------------
// Corner Flags — tall whip poles, quartered flags that flutter and stream
// with speed; the poles whip on acceleration.
// ---------------------------------------------------------------------------
export const flags: PartFn = (a, c, m, opts) => {
  const s = a.s;
  const group = new THREE.Group();
  const bases = flagBases(a, opts.surface, opts.mods);
  const poles: { pole: THREE.Group; cloth: ClothHandle; sx: number }[] = [];
  const h = POLE_H * s;
  for (const { sx, x, z } of bases) {
    const pole = new THREE.Group();
    // The rubber foot stands on the highest point of the body under it.
    pole.position.set(x, opts.surface?.topIn(x - 0.06 * s, x + 0.06 * s, z - 0.06 * s, z + 0.06 * s) ?? a.deckY, z);
    const pb = new PartBuilder();
    pb.cyl(0.016 * s, 0.016 * s, h, 0, h / 2, 0, WHITE);
    pb.cyl(0.03 * s, 0.06 * s, 0.07 * s, 0, 0.035 * s, 0, RUBBER);
    pb.sphere(0.03 * s, 0, h, 0, GOLD, { bucket: 'metal' });
    pole.add(pb.build(m));
    const mat = m.track(new THREE.MeshStandardMaterial({ map: m.trackTex(cornerFlagTexture(c.primary, c.secondary)), side: THREE.DoubleSide, roughness: 0.8 }));
    const cloth = clothMesh(CLOTH_L * s, CLOTH_W * s, 14, 8, mat);
    cloth.mesh.position.set(0, h - 0.02 * s, 0);
    cloth.mesh.rotation.y = Math.PI / 2 - sx * flagSplay(0);
    pole.add(cloth.mesh);
    poles.push({ pole, cloth, sx });
    group.add(pole);
  }
  return {
    group,
    update: (ctx, dt): void => {
      const wind = windOf(ctx);
      const whip = clamp(-ctx.accel * 0.02, -0.3, 0.3);
      for (const { pole, cloth: cl, sx } of poles) {
        pole.rotation.x = damp(pole.rotation.x, whip, 4, dt);
        cl.mesh.rotation.y = Math.PI / 2 - sx * flagSplay(wind);
        cl.amp.value = 0.02 * s + 0.06 * s * wind;
        cl.droop.value = DROOP * s * (1 - wind);
        cl.time.value += dt * (0.9 + 2.5 * wind);
      }
    },
  };
};
